import { useMutation, useApolloClient, ApolloError, useLazyQuery } from '@apollo/client'
import { Formik, useFormikContext } from 'formik'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Fragment, ReactElement, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { Column } from '@10xLabs/react-web/components/Column'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { Row } from '@10xLabs/react-web/components/Row'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { CenteredText } from 'components/Text'
import { VehicleAutocomplete } from 'features/Vehicles/components/VehicleAutocomplete'
import { CHANGE_LOCAL_TRIP_VEHICLE } from 'features/Trips/mutations-legacy'
import { SCHEDULE_ROUTE_SERVICE_TRIPS } from 'features/Services/mutations-legacy'
import {
  GTTVehicleAutocomplete,
  GTTripV2,
  GTTripV2Status,
  GTTripsVehicleConflictResponse,
  GTVehicleSuggestion
} from 'gql/types.generated'
import { getVehicleLabelWithExternalId } from 'features/Vehicles/utils'
import { getTripDateForOverride } from 'features/Trips/utils'
import { IChangeVehicleForm } from 'features/Trips/types'
import { resolvers as tripsResolvers } from 'resolvers/trips'
import { schema } from './schema'
import { Select } from 'components/v2/Select'
import { safeFormatMessage } from '@10xlabs/utils'
import { Textarea } from 'components/v2/Textarea'
import { isEmpty, keys } from 'lodash'
import { getUnprocessableErrors, isNotSameVehicleCompany } from 'utils/error'
import { GET_TRIPS_VEHICLE_CONFLICT } from 'features/Trips/queries-legacy'
import { WarningNotification } from 'components/WarningNotification'
import { DATE_FORMAT } from 'utils/date'
import { addMonths, format } from 'date-fns'
import { getShortId } from 'utils/string'
import { IChangedEntityWithAllPromise } from 'types/index'
import { CREATE_SERVICE_PAST_VEHICLE_OVERRIDE, CREATE_SERVICE_VEHICLE_OVERRIDE } from 'features/Services/mutations'
import {
  CreateRouteServiceVehicleOverrideReasonCategory,
  CreateRouteServiceVehicleOverrideReasonSubcategory
} from 'gql/generated/graphql'
import { useLogin } from 'features/Login/hooks'
import { canChangePastTripVehicle, canChangeTripVehicle } from 'features/Users/utils'

const messages = defineMessages({
  selectVehicle: {
    id: 'trips.changeVehicle.selectVehicle',
    defaultMessage: 'Select new vehicle'
  },
  categoryPlaceholder: {
    id: 'trips.changeVehicle.category.required',
    defaultMessage: 'Select a category'
  },
  subcategoryPlaceholder: {
    id: 'trips.changeVehicle.subcategory.required',
    defaultMessage: 'Select a subcategory'
  },
  commentsPlaceholder: {
    id: 'trips.changeVehicle.comments.placeholder',
    defaultMessage: 'Add comments'
  },
  notChangedTrips: {
    id: 'trips.changeVehicle.notChangedTrips',
    defaultMessage: 'This vehicle is not associated with the company of the trip: {tripId}.'
  },
  changeVehicleGenericError: {
    id: 'trips.changeVehicle.genericError',
    defaultMessage: 'An error occurred while changing the vehicle for trip {tripId}.'
  }
})

const changeVehicleReasonsMessages = defineMessages({
  [CreateRouteServiceVehicleOverrideReasonCategory.Partner]: {
    id: 'trips.changeVehicle.category.partner',
    defaultMessage: 'Partner'
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.BusinessClient]: {
    id: 'trips.changeVehicle.category.businessClient',
    defaultMessage: 'Business Client'
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.Vehicle]: {
    id: 'trips.changeVehicle.category.vehicle',
    defaultMessage: 'Vehicle'
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.Planning]: {
    id: 'trips.changeVehicle.category.planning',
    defaultMessage: 'Planning'
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.Other]: {
    id: 'trips.changeVehicle.category.other',
    defaultMessage: 'Other'
  }
})
const changeVehicleSubcategoryMessages = defineMessages({
  [CreateRouteServiceVehicleOverrideReasonSubcategory.Accident]: {
    id: 'trips.changeVehicle.subcategory.accident',
    defaultMessage: 'Accident'
  },
  [CreateRouteServiceVehicleOverrideReasonSubcategory.MechanicalFailure]: {
    id: 'trips.changeVehicle.subcategory.mechanicalFailure',
    defaultMessage: 'Mechanical failure'
  },
  [CreateRouteServiceVehicleOverrideReasonSubcategory.Other]: {
    id: 'trips.changeVehicle.subcategory.other',
    defaultMessage: 'Other'
  },
  [CreateRouteServiceVehicleOverrideReasonSubcategory.ScheduledMaintenance]: {
    id: 'trips.changeVehicle.subcategory.scheduledMaintenance',
    defaultMessage: 'Scheduled maintenance'
  }
})

const DateWrapper = styled.div`
  display: flex;
  margin: 3px 0;
`

const TripLink = styled(NavLink)`
  font-weight: 600;
  color: #5d59d7;
`

export const CHANGE_VEHICLE_REASONS = {
  [CreateRouteServiceVehicleOverrideReasonCategory.Vehicle]: {
    subcategories: [
      CreateRouteServiceVehicleOverrideReasonSubcategory.Accident,
      CreateRouteServiceVehicleOverrideReasonSubcategory.MechanicalFailure,
      CreateRouteServiceVehicleOverrideReasonSubcategory.Other,
      CreateRouteServiceVehicleOverrideReasonSubcategory.ScheduledMaintenance
    ]
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.Partner]: {
    subcategories: []
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.BusinessClient]: {
    subcategories: []
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.Other]: {
    subcategories: []
  },
  [CreateRouteServiceVehicleOverrideReasonCategory.Planning]: {
    subcategories: []
  }
}

interface IChangeTripsVehicleFormProps {
  trips: GTTripV2[]
  loading: boolean
  errorsMessages: string[]
  onCancel: () => void
}

const ChangeTripsVehicleForm = ({
  trips,
  loading,
  errorsMessages,
  onCancel
}: IChangeTripsVehicleFormProps): ReactElement => {
  const firstTrip = trips[0]
  const isModifyingSingleTrip = trips.length === 1
  const intl = useIntl()
  const { setFieldValue, handleChange, handleSubmit, handleBlur, touched, errors, values, dirty } =
    useFormikContext<IChangeVehicleForm>()
  const [getTripsVehicleConflict, { data: tripsVehicleConflictData, loading: tripsVehicleConflictLoading }] =
    useLazyQuery<{ tripsVehicleConflict: GTTripsVehicleConflictResponse }>(GET_TRIPS_VEHICLE_CONFLICT, {
      fetchPolicy: 'network-only'
    })
  useEffect(() => {
    const shouldVerifyPotentialConflicts =
      isModifyingSingleTrip && values.vehicle.id && values.vehicle.id !== firstTrip.vehicle?.id
    if (shouldVerifyPotentialConflicts) {
      getTripsVehicleConflict({
        variables: {
          input: {
            tripID: firstTrip.id,
            vehicleID: values.vehicle.id
          }
        }
      })
    }
  }, [firstTrip.id, values.vehicle.id, getTripsVehicleConflict, isModifyingSingleTrip, firstTrip.vehicle?.id])

  const totalConflict = tripsVehicleConflictData?.tripsVehicleConflict?.total || 0
  const tripsWithConflict = tripsVehicleConflictData?.tripsVehicleConflict?.items || []
  const tripsWithConflictIds = tripsWithConflict.map((trip) => trip.id)
  const today = new Date()
  const firstStop = firstTrip.itinerary.at(0)
  const lastStop = firstTrip.itinerary.at(-1)
  const categorysOptions =
    CHANGE_VEHICLE_REASONS[values.category as CreateRouteServiceVehicleOverrideReasonCategory]?.subcategories || []

  return (
    <form onSubmit={handleSubmit}>
      <Modal.Content>
        {isModifyingSingleTrip && (
          <Row>
            <Column>
              <div>
                <FormattedMessage
                  id="trip.changeVehicle.confirmation.tripDetails"
                  defaultMessage="Trip from <strong>{origin}</strong> to <strong>{destination}</strong>"
                  values={{
                    origin: firstStop?.place.name,
                    destination: lastStop?.place.name,
                    strong: (msg) => <strong>{msg}</strong>
                  }}
                />
              </div>
              <DateWrapper>
                <FormattedDate intl={intl} value={firstTrip.departureAt} month="2-digit" day="2-digit" year="numeric" />
                &nbsp;
                <FormattedTimeByDate
                  intl={intl}
                  date={firstTrip.departureAt}
                  timeZone={firstStop?.place.timezone || undefined}
                />
              </DateWrapper>
            </Column>
          </Row>
        )}
        <Row>
          <Column>
            <VehicleAutocomplete
              id="vehicle.id"
              placeholder={intl.formatMessage(messages.selectVehicle)}
              value={getVehicleLabelWithExternalId(values.vehicle as GTTVehicleAutocomplete)}
              touched={touched.vehicle?.id}
              errors={errors.vehicle?.id}
              onChange={(vehicle: GTVehicleSuggestion): void => {
                setFieldValue('vehicle', {
                  id: vehicle.id,
                  name: vehicle.name,
                  externalID: vehicle.externalID
                })
              }}
              label={<FormattedMessage id="trips.changeVehicle.newVehicle.label" defaultMessage="New vehicle" />}
              disabled={tripsVehicleConflictLoading}
              isActive
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Select
              id="category"
              value={values.category}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.category}
              errors={errors.category}
              label={<FormattedMessage id="trips.changeVehicle.form.category.label" defaultMessage="Reason" />}
            >
              <option value="">{intl.formatMessage(messages.categoryPlaceholder)}</option>
              {keys(CHANGE_VEHICLE_REASONS).map((category): ReactElement => {
                return (
                  <option key={category} value={category}>
                    {safeFormatMessage({ intl, messages: changeVehicleReasonsMessages, key: category })}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column>
            <Select
              id="subcategory"
              value={values.subcategory}
              disabled={categorysOptions.length === 0}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.subcategory}
              errors={errors.subcategory}
              label={<FormattedMessage id="trips.changeVehicle.form.subcategory.label" defaultMessage="Subcategory" />}
            >
              <option value="">{intl.formatMessage(messages.subcategoryPlaceholder)}</option>
              {categorysOptions?.map((subcategory): ReactElement => {
                return (
                  <option key={subcategory} value={subcategory}>
                    {safeFormatMessage({ intl, messages: changeVehicleSubcategoryMessages, key: subcategory })}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column>
            <Textarea
              id="comments"
              label={<FormattedMessage id="trips.cancellation.form.comments.label" defaultMessage="Comments" />}
              placeholder={intl.formatMessage(messages.commentsPlaceholder)}
              value={values.comments || ''}
              onChange={handleChange}
            />
          </Column>
        </Row>
        {isModifyingSingleTrip ? (
          <Row>
            <Column>
              <CenteredText>
                <FormattedMessage
                  id="trips.changeVehicle.confirmation.text"
                  defaultMessage="Are you sure you want to change the vehicle?"
                />
              </CenteredText>
            </Column>
          </Row>
        ) : (
          <Row>
            <Column>
              <CenteredText>
                <FormattedMessage
                  id="trips.changeVehicle.confirmation.tripDetails"
                  defaultMessage="Are you sure want to change vehicle to {tripsCount} {tripsCount, plural,
                  one {trip}
                  other {trips}
                }?"
                  values={{
                    tripsCount: trips.length
                  }}
                />
              </CenteredText>
            </Column>
          </Row>
        )}
        {totalConflict > 0 && (
          <Row>
            <Column>
              <WarningNotification>
                <FormattedMessage
                  id="trips.changeVehicle.conflict.desc"
                  defaultMessage="<strong>Vehicle {vehicleName} will merge with trip</strong> <tripIds></tripIds>. Do you want to continue with the adjustment?"
                  values={{
                    vehicleName: values.vehicle.name,
                    strong: (msg) => <strong>{msg}</strong>,
                    tripIds: () => (
                      <TripLink
                        to={`/trips?ids=${tripsWithConflictIds
                          .map((id) => getShortId(id))
                          .join(',')}&departureDateFrom=${format(
                          addMonths(today, -1),
                          DATE_FORMAT
                        )}&departureDateTo=${format(addMonths(today, 3), DATE_FORMAT)}`}
                      >
                        {tripsWithConflict.map((trip, index) => {
                          return (
                            <Fragment key={trip.id}>
                              {trip.id.substring(0, 8)}
                              {index < tripsWithConflict.length - 1 && ', '}
                            </Fragment>
                          )
                        })}
                      </TripLink>
                    )
                  }}
                />
              </WarningNotification>
            </Column>
          </Row>
        )}
      </Modal.Content>
      <Modal.Footer>
        {!isEmpty(errorsMessages) && (
          <>
            {errorsMessages.map((msg, index) => (
              <Modal.ErrorMessage key={index}>
                <ErrorMessage>{msg}</ErrorMessage>
              </Modal.ErrorMessage>
            ))}
          </>
        )}
        <Modal.Actions>
          <Button type="button" kind="cancel" onClick={onCancel}>
            <FormattedMessage id="modal.actions.close" defaultMessage="No, close" />
          </Button>
          <Button kind="primary" loading={loading} disabled={!dirty || tripsVehicleConflictLoading}>
            <FormattedMessage id="trips.changeVehicle.action" defaultMessage="Change vehicle" />
          </Button>
        </Modal.Actions>
      </Modal.Footer>
    </form>
  )
}

interface IChangeTripsVehicleModalProps {
  trips: GTTripV2[]
  closeModal: () => void
  onSuccess?: () => void
}

export const ChangeTripsVehicleModal = ({
  trips,
  closeModal,
  onSuccess
}: IChangeTripsVehicleModalProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  const {
    accessToken: { policies }
  } = useLogin()
  const canChangeVehicle = canChangeTripVehicle(policies)
  const canChangePastVehicle = canChangePastTripVehicle(policies)
  client.addResolvers(tripsResolvers)

  const [errorsMessages, setErrorsMessages] = useState<string[]>([])

  const [changeLocalTripVehicle] = useMutation(CHANGE_LOCAL_TRIP_VEHICLE)
  const [createServiceVehicleOverride, { loading: createServiceVehicleOverrideLoading }] = useMutation(
    CREATE_SERVICE_VEHICLE_OVERRIDE
  )
  const [createServicePastVehicleOverride, { loading: createServicePastVehicleOverrideLoading }] = useMutation(
    CREATE_SERVICE_PAST_VEHICLE_OVERRIDE
  )
  const [scheduleServiceTrips, { loading: scheduleServiceTripsLoading }] = useMutation(SCHEDULE_ROUTE_SERVICE_TRIPS)

  const changeTripVehicle = async (trip: GTTripV2, values: IChangeVehicleForm): Promise<void> => {
    const isTripCompleted = trip.status === GTTripV2Status.Completed
    const tripDepartureAt = getTripDateForOverride(trip)
    const shouldChangeVehicle = (isTripCompleted && canChangePastVehicle) || (!isTripCompleted && canChangeVehicle)

    if (!shouldChangeVehicle) {
      return
    }

    if (isTripCompleted && canChangePastVehicle) {
      await createServicePastVehicleOverride({
        variables: {
          input: {
            id: trip.routeService.id,
            date: tripDepartureAt,
            vehicleID: values.vehicle.id,
            reason: {
              category: values.category as CreateRouteServiceVehicleOverrideReasonCategory,
              subcategory: (values.subcategory as CreateRouteServiceVehicleOverrideReasonSubcategory) || null
            },
            comments: values.comments || null
          }
        }
      })
    } else if (!isTripCompleted && canChangeVehicle) {
      await createServiceVehicleOverride({
        variables: {
          input: {
            id: trip.routeService.id,
            date: tripDepartureAt,
            vehicleID: values.vehicle.id,
            reason: {
              category: values.category as CreateRouteServiceVehicleOverrideReasonCategory,
              subcategory: (values.subcategory as CreateRouteServiceVehicleOverrideReasonSubcategory) || null
            },
            comments: values.comments || null
          }
        }
      })
    }
    await scheduleServiceTrips({
      variables: {
        input: {
          id: trip.routeService.id,
          scheduleStartAt: tripDepartureAt,
          scheduleEndAt: tripDepartureAt
        }
      }
    })
    changeLocalTripVehicle({
      variables: {
        tripId: trip.id,
        vehicle: {
          ...values.vehicle,
          schedule: trip.vehicle?.schedule
        }
      }
    })
  }

  const handleSubmit = async (values: IChangeVehicleForm): Promise<void> => {
    setErrorsMessages([])
    const shouldChangeVehicle = trips.length > 1 || trips[0].vehicle?.id !== values.vehicle.id
    if (shouldChangeVehicle) {
      try {
        const changeVehicles = trips.map((trip): Promise<void> => changeTripVehicle(trip, values))
        const changeVehiclesResults = await Promise.allSettled(changeVehicles)
        const tripsWithResult = trips.map((trip, index) => ({
          ...trip,
          ...changeVehiclesResults[index]
        }))
        const notChangedTrips: IChangedEntityWithAllPromise[] = tripsWithResult.filter(
          ({ status }) => status === 'rejected'
        )

        if (!isEmpty(notChangedTrips)) {
          setErrorsMessages(
            notChangedTrips.map(({ reason, id }): string => {
              const errors = getUnprocessableErrors(reason as ApolloError)
              const hasNotSameVehicleCompanyError = errors.some(isNotSameVehicleCompany)
              const shortId = getShortId(id)
              if (hasNotSameVehicleCompanyError) {
                return intl.formatMessage(messages.notChangedTrips, {
                  tripId: shortId
                })
              } else {
                return intl.formatMessage(messages.changeVehicleGenericError, {
                  tripId: shortId
                })
              }
            })
          )
        } else {
          closeModal()
          onSuccess?.()
        }
      } catch (error) {
        // Do nothing
      }
    }
  }

  const isModifyingSingleTrip = trips.length === 1
  const firstTrip = trips[0]

  const initialVehicle: IChangeVehicleForm['vehicle'] =
    isModifyingSingleTrip && firstTrip.vehicle
      ? {
          id: firstTrip.vehicle.id,
          name: firstTrip.vehicle.name,
          externalID: firstTrip.vehicle.externalID
        }
      : {
          id: '',
          name: '',
          externalID: ''
        }

  const initialValues: IChangeVehicleForm = {
    vehicle: initialVehicle,
    category: '',
    subcategory: '',
    comments: ''
  }

  const loading =
    createServicePastVehicleOverrideLoading || createServiceVehicleOverrideLoading || scheduleServiceTripsLoading

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          {isModifyingSingleTrip ? (
            <FormattedMessage
              id="trips.changeVehicle.title"
              defaultMessage="Change vehicle for trip {id}"
              values={{
                id: firstTrip.id.substring(0, 8)
              }}
            />
          ) : (
            <FormattedMessage id="trips.changeVehicle.title" defaultMessage="Change vehicle" />
          )}
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(): ReactElement => {
          return (
            <ChangeTripsVehicleForm
              trips={trips}
              loading={loading}
              errorsMessages={errorsMessages}
              onCancel={(): void => closeModal()}
            />
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
